/**
 * SavedViewsSettingsPage — manage fias_leads_saved_views.
 * Each view is a per-entity (contact/company/deal/task) filter+sort
 * preset. Inline list with create/delete; integration with the actual
 * list pages (applying a saved view) is a follow-up.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsSavedViews,
  useCreateFiasLeadsSavedView,
  useDeleteFiasLeadsSavedView,
} from '../hooks/useFiasLeads';
import type { FiasLeadsSavedViewEntityType } from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const ENTITY_TYPES: FiasLeadsSavedViewEntityType[] = ['contact', 'company', 'deal', 'task'];

export function SavedViewsSettingsPage({ holderId }: Props) {
  const [activeEntity, setActiveEntity] = useState<FiasLeadsSavedViewEntityType>('contact');
  const viewsQuery = useFiasLeadsSavedViews(holderId, activeEntity);
  const createView = useCreateFiasLeadsSavedView(holderId);
  const deleteView = useDeleteFiasLeadsSavedView(holderId);

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{
    name: string;
    filtersJson: string;
    sortBy: string;
    sortDir: 'asc' | 'desc';
    isDefault: boolean;
  }>({ name: '', filtersJson: '{}', sortBy: '', sortDir: 'desc', isDefault: false });
  const [jsonError, setJsonError] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    let filters: Record<string, unknown> = {};
    try {
      filters = draft.filtersJson.trim() ? JSON.parse(draft.filtersJson) : {};
    } catch (err) {
      setJsonError((err as Error).message);
      return;
    }
    setJsonError('');
    await createView.mutateAsync({
      entityType: activeEntity,
      name: draft.name.trim(),
      filters,
      sortBy: draft.sortBy || null,
      sortDir: draft.sortDir,
      isDefault: draft.isDefault,
    });
    setDraft({ name: '', filtersJson: '{}', sortBy: '', sortDir: 'desc', isDefault: false });
    setShowCreate(false);
  };

  const views = viewsQuery.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <Link
        to={`${ARCHE_PATH}/settings`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Settings
      </Link>

      <header>
        <h1 className="text-2xl font-semibold">Saved views</h1>
        <p className="text-sm text-muted-foreground">
          Per-entity filter + sort presets. Useful for "My open deals", "VIP contacts", etc.
        </p>
      </header>

      <div className="flex gap-1 rounded border border-border bg-card p-1 w-fit">
        {ENTITY_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setActiveEntity(t)}
            className={
              'rounded px-3 py-1 text-sm transition-colors ' +
              (activeEntity === t
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
        >
          <Plus className="h-4 w-4" />
          New view
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="rounded border border-border bg-card p-4 flex flex-col gap-2"
        >
          <input
            type="text"
            placeholder="View name *"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="rounded border border-border bg-background px-3 py-2 text-sm"
            required
            autoFocus
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Filters (JSON)</span>
            <textarea
              value={draft.filtersJson}
              onChange={(e) => setDraft((d) => ({ ...d, filtersJson: e.target.value }))}
              rows={3}
              className="rounded border border-border bg-background px-3 py-2 text-sm font-mono"
            />
            {jsonError && <span className="text-xs text-destructive">{jsonError}</span>}
          </label>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Sort by (e.g. updated_at)"
              value={draft.sortBy}
              onChange={(e) => setDraft((d) => ({ ...d, sortBy: e.target.value }))}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            />
            <select
              value={draft.sortDir}
              onChange={(e) =>
                setDraft((d) => ({ ...d, sortDir: e.target.value as 'asc' | 'desc' }))
              }
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
              />
              Default
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.name.trim() || createView.isPending}
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createView.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {viewsQuery.isLoading && <SkeletonBlock lines={3} />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {views.map((v) => (
            <li key={v.id} className="p-3 flex items-center gap-3 group">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {v.name}
                  {v.is_default && (
                    <span className="ml-2 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      default
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Sort: {v.sort_by ?? 'default'} {v.sort_dir ?? ''} · Filters:{' '}
                  {Object.keys(v.filters).length} key(s)
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm(`Delete view "${v.name}"?`)) {
                    deleteView.mutateAsync(v.id);
                  }
                }}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {views.length === 0 && !viewsQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            No saved views for {activeEntity}s yet.
          </p>
        )}
      </div>
    </div>
  );
}
